/**
 * ExamBookingsTab — Admin management of exam booking requests.
 *
 * Filter by status / nationality / exam type. Per row: status dropdown
 * (new → contacted → booked → confirmed → passed) saved on change, and an
 * inline-editable notes textarea saved explicitly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Check, X, StickyNote, Trash2 } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';

const STATUSES = ['new', 'contacted', 'booked', 'confirmed', 'passed'];

const STATUS_STYLES = {
  new:       'bg-blue-500/20 text-blue-300 border-blue-500/40',
  contacted: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  booked:    'bg-purple-500/20 text-purple-300 border-purple-500/40',
  confirmed: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  passed:    'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAdminToken()}`,
});

export const ExamBookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('');
  const [nationalityFilter, setNationalityFilter] = useState('');
  const [examFilter, setExamFilter] = useState('');

  // Notes editing — one row at a time
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline delete confirmation — which row is pending delete
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (nationalityFilter) params.set('nationality', nationalityFilter);
      if (examFilter) params.set('exam_code', examFilter);
      const res = await fetch(
        `${API_BASE}/api/admin/exam-bookings?${params.toString()}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error('Failed to load bookings');
      setBookings(await res.json());
    } catch (e) {
      console.error(e);
      toast.error('Failed to load exam bookings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, nationalityFilter, examFilter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/exam-pricing`, { headers: authHeaders() });
        if (res.ok) setExams(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchExams();
  }, []);

  const updateBooking = async (id, payload, successMsg) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-bookings/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Update failed');
      }
      const updated = await res.json();
      setBookings((rows) => rows.map((r) => (r.id === id ? updated : r)));
      toast.success(successMsg);
    } catch (e) {
      toast.error(e.message);
      fetchBookings(); // revert optimistic UI on failure
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (booking, newStatus) => {
    if (newStatus === booking.status) return;
    updateBooking(booking.id, { status: newStatus }, `${booking.full_name} → ${newStatus}`);
  };

  const startNotesEdit = (booking) => {
    setEditingNotesId(booking.id);
    setNotesValue(booking.notes || '');
  };

  const saveNotes = (booking) => {
    updateBooking(booking.id, { notes: notesValue }, 'Notes saved');
    setEditingNotesId(null);
  };

  const deleteBooking = async (booking) => {
    setConfirmDeleteId(null);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-bookings/${booking.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Delete failed');
      }
      setBookings((rows) => rows.filter((r) => r.id !== booking.id));
      toast.success(`Deleted request from ${booking.full_name}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const examName = (code) => exams.find((e) => e.exam_code === code)?.exam_name || code;

  const selectCls = 'bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50';

  return (
    <div className="space-y-6">
      {/* Header + filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Exam Bookings</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {bookings.length} request(s)
            {statusFilter || nationalityFilter || examFilter ? ' (filtered)' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={nationalityFilter} onChange={(e) => setNationalityFilter(e.target.value)} className={selectCls}>
            <option value="">All countries</option>
            <option value="india">🇮🇳 India</option>
            <option value="international">🌍 International</option>
          </select>
          <select value={examFilter} onChange={(e) => setExamFilter(e.target.value)} className={selectCls}>
            <option value="">All exams</option>
            {exams.map((e) => <option key={e.exam_code} value={e.exam_code}>{e.exam_name}</option>)}
          </select>
          <button onClick={fetchBookings} className="p-2 text-slate-400 hover:text-white rounded-lg border border-slate-600 transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : bookings.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">No booking requests found.</p>
      ) : (
        <div className="overflow-x-auto bg-slate-800/60 border border-slate-700/50 rounded-2xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-700/50">
                <th className="text-left py-3 px-3 max-w-[120px]">Name</th>
                <th className="text-left py-3 px-3 max-w-[150px]">Email</th>
                <th className="text-left py-3 px-3 max-w-[110px]">Phone</th>
                <th className="text-left py-3 px-3 max-w-[80px]">Country</th>
                <th className="text-left py-3 px-3 max-w-[120px]">Exam</th>
                <th className="text-left py-3 px-3 max-w-[80px]">Month</th>
                <th className="text-left py-3 px-3 max-w-[90px]">Price</th>
                <th className="text-left py-3 px-3 max-w-[130px]">Submitted</th>
                <th className="text-left py-3 px-3 max-w-[120px]">Status</th>
                <th className="text-left py-3 px-3 max-w-[150px]">Notes</th>
                <th className="text-left py-3 px-3 max-w-[80px]">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {bookings.map((b) => (
                <tr key={b.id} className="hover:bg-slate-800/30 transition-colors align-top">
                  <td className="py-3 px-3 text-white font-medium max-w-[120px]">
                    <span className="block truncate" title={b.full_name}>{b.full_name}</span>
                    {b.source === 'email_trigger' && (
                      <span className="block text-[10px] text-orange-400 mt-0.5">via email</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-slate-300 max-w-[150px] truncate" title={b.email}>{b.email}</td>
                  <td className="py-3 px-3 text-slate-300 max-w-[110px] truncate" title={b.phone}>{b.phone}</td>
                  <td className="py-3 px-3 max-w-[80px] whitespace-nowrap">{b.nationality === 'india' ? '🇮🇳 India' : '🌍 Intl'}</td>
                  <td className="py-3 px-3 text-slate-300 max-w-[120px] truncate" title={examName(b.exam_code)}>{examName(b.exam_code)}</td>
                  <td className="py-3 px-3 text-slate-300 max-w-[80px] whitespace-nowrap">{b.preferred_month}</td>
                  <td className="py-3 px-3 text-slate-300 max-w-[90px] truncate" title={b.discounted_price_shown != null ? `₹${Number(b.discounted_price_shown).toLocaleString('en-IN')}` : ''}>
                    {b.discounted_price_shown != null
                      ? `₹${Number(b.discounted_price_shown).toLocaleString('en-IN')}`
                      : '—'}
                  </td>
                  <td className="py-3 px-3 text-slate-400 text-xs max-w-[130px] truncate" title={formatDate(b.submitted_at)}>{formatDate(b.submitted_at)}</td>
                  <td className="py-3 px-3 max-w-[120px]">
                    <select
                      value={b.status}
                      disabled={saving}
                      onChange={(e) => handleStatusChange(b, e.target.value)}
                      className={`text-xs px-2 py-1.5 rounded-lg border bg-slate-900/60 focus:outline-none ${STATUS_STYLES[b.status] || 'text-slate-300 border-slate-600'}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-3 max-w-[150px]">
                    {editingNotesId === b.id ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          rows={3}
                          maxLength={2000}
                          autoFocus
                          className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-orange-500/50"
                        />
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => saveNotes(b)} disabled={saving} className="p-1 text-emerald-400 hover:bg-emerald-500/20 rounded" title="Save"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingNotesId(null)} className="p-1 text-slate-400 hover:bg-slate-700 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => startNotesEdit(b)}
                        className="flex items-start gap-1.5 text-left text-xs text-slate-400 hover:text-white transition-colors w-full"
                        title={b.notes ? `${b.notes}\n\n(Click to edit)` : 'Click to add notes'}
                      >
                        <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
                        <span className="truncate">{b.notes || 'Add note…'}</span>
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-3 max-w-[80px]">
                    {confirmDeleteId === b.id ? (
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-xs text-red-400 font-medium">Delete?</span>
                        <div className="flex items-center gap-1">
                        <button
                          onClick={() => deleteBooking(b)}
                          disabled={saving}
                          className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={saving}
                          className="text-xs px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-white disabled:opacity-50"
                        >
                          No
                        </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(b.id)}
                        disabled={saving}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete booking request"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExamBookingsTab;
